import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteM360Diagnostic,
  getM360Status,
  getTicketM360Diagnostics,
  retrieveM360Diagnostic,
  type M360DiagnosticRow,
  type M360LookupKind,
} from '@/lib/api/m360.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatDateTime } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

function detectLookupKind(value: string): Exclude<M360LookupKind, 'auto'> {
  const v = value.trim();
  if (/^\d{15}$/.test(v)) return 'imei';
  if (v.includes('-') && v.length === 36) return 'sessionId';
  if (/^[a-zA-Z0-9]{3,20}$/.test(v) && !/^\d+$/.test(v)) return 'm360id';
  return 'serial';
}

function rowBattery(row: M360DiagnosticRow) {
  const full = (row.fullResults ?? {}) as { battery?: M360DiagnosticRow['battery'] };
  return row.battery ?? full.battery ?? null;
}

function rowSecurity(row: M360DiagnosticRow) {
  const full = (row.fullResults ?? {}) as { security?: M360DiagnosticRow['security'] };
  return row.security ?? full.security ?? null;
}

function rowGrading(row: M360DiagnosticRow) {
  const full = (row.fullResults ?? {}) as { grading?: M360DiagnosticRow['grading'] };
  return row.grading ?? full.grading ?? null;
}

function rowTests(row: M360DiagnosticRow) {
  const full = (row.fullResults ?? {}) as { tests?: M360DiagnosticRow['tests'] };
  return row.tests ?? full.tests ?? [];
}

function rowDeviceName(row: M360DiagnosticRow): string {
  const full = (row.fullResults ?? {}) as {
    deviceInfo?: { model?: string; marketingName?: string | null; make?: string };
  };
  const di = row.deviceInfo ?? full.deviceInfo;
  return (
    di?.marketingName ||
    di?.model ||
    [di?.make, di?.model].filter(Boolean).join(' ') ||
    row.deviceModel ||
    'Device'
  );
}

function blacklistLabel(result: string | null | undefined): { text: string; tone: 'ok' | 'bad' | 'warn' } {
  const r = (result ?? '').toLowerCase();
  if (r === 'green') return { text: 'GREEN — clean', tone: 'ok' };
  if (r === 'red') return { text: 'RED — blacklisted', tone: 'bad' };
  if (r) return { text: `${result!.toUpperCase()} — unknown`, tone: 'warn' };
  return { text: 'Not checked', tone: 'warn' };
}

function formatSessionDate(row: M360DiagnosticRow): string {
  const ts = row.connectionTime ?? row.completedAt ?? row.createdAt;
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return formatDateTime(ts);
  }
}

function DiagnosticCard({
  title,
  row,
  onRemove,
  onApplyGrading,
}: {
  title: string;
  row: M360DiagnosticRow;
  onRemove: () => void;
  onApplyGrading?: (condition: 'excellent' | 'good' | 'fair' | 'poor' | 'broken') => void;
}) {
  const battery = rowBattery(row);
  const security = rowSecurity(row);
  const grading = rowGrading(row);
  const tests = rowTests(row);
  const failed = tests.filter((t) => t.status === 'fail');
  const passed = row.passedTests ?? tests.filter((t) => t.status === 'pass').length;
  const failedCount = row.failedTests ?? failed.length;
  const skipped =
    row.skippedTests ?? tests.filter((t) => t.status === 'skip').length;

  const health =
    battery?.preciseHealthPercent ??
    battery?.healthPercent ??
    row.batteryHealthPercent;
  const cycles = battery?.cycleCount ?? row.batteryCycleCount;
  const design = battery?.designCapacityMah;
  const fullCap = battery?.fullChargeCapacityMah;

  const htmlUrl =
    row.htmlReportUrl ??
    (row.fullResults as { htmlReportUrl?: string } | null)?.htmlReportUrl ??
    null;
  const pdfUrl =
    row.pdfReportUrl ??
    (row.fullResults as { pdfReportUrl?: string } | null)?.pdfReportUrl ??
    null;
  const reportUrl =
    row.reportUrl ??
    (row.fullResults as { reportUrl?: string } | null)?.reportUrl ??
    htmlUrl ??
    pdfUrl;

  const bl = blacklistLabel(security?.blacklist);
  const accountFound = (security?.associatedAccount ?? '').toLowerCase() === 'found';
  const nonOriginal = security?.nonOriginalParts ?? [];
  const oemIssue =
    security?.oemIsAuthentic === false || nonOriginal.length > 0;
  const simLocked =
    security?.simLockIsLocked === true ||
    (security?.simLock ?? '').toLowerCase().includes('lock');

  const suggested =
    row.suggestedCondition ??
    null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-bold uppercase tracking-wide text-slate-900">
            {title} — {rowDeviceName(row)}
          </h4>
          <p className="mt-0.5 text-xs text-slate-500">
            Session {formatSessionDate(row)}
            {row.username ? ` · Tech: ${row.username}` : ''}
          </p>
        </div>
        {row.overallGrade ? (
          <span className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-800">
            Grade {row.overallGrade}
          </span>
        ) : null}
      </div>

      <div className="mt-3 border-t border-slate-100 pt-3 space-y-2 text-sm">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-medium text-slate-800">🔋 Battery</span>
          <span className="text-slate-900">
            {health != null ? `${Math.round(Number(health))}%` : '—'}
            {cycles != null ? `  ·  ${cycles} cycles` : ''}
          </span>
          {(fullCap != null || design != null) && (
            <span className="text-xs text-slate-500">
              {fullCap != null ? fullCap : '—'}
              {design != null ? ` / ${design} mAh` : ' mAh'}
            </span>
          )}
        </div>

        <div
          className={cn(
            'flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5',
            bl.tone === 'ok' && 'bg-emerald-50 text-emerald-900',
            bl.tone === 'bad' && 'bg-red-100 text-red-900 ring-1 ring-red-300',
            bl.tone === 'warn' && 'bg-amber-50 text-amber-900',
          )}
        >
          <span className="font-medium">🛡 Blacklist</span>
          <span
            className={cn(
              'inline-block h-2 w-2 rounded-full',
              bl.tone === 'ok' && 'bg-emerald-500',
              bl.tone === 'bad' && 'bg-red-600',
              bl.tone === 'warn' && 'bg-amber-500',
            )}
          />
          <span className="font-semibold">{bl.text}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-slate-800">
          <span className="font-medium">📶 SIM Lock</span>
          <span>
            {security?.simLockIsLocked === false
              ? 'Unlocked'
              : simLocked
                ? `Locked${security?.simLockCarrier ? ` (${security.simLockCarrier})` : ''}`
                : security?.simLock ?? 'Not checked'}
          </span>
        </div>

        <div
          className={cn(
            'flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5',
            oemIssue
              ? 'bg-amber-50 text-amber-950 ring-1 ring-amber-300'
              : 'text-slate-800',
          )}
        >
          <span className="font-medium">🔧 OEM Parts</span>
          <span>
            {oemIssue ? (
              <>
                ⚠{' '}
                {nonOriginal.length
                  ? nonOriginal.map((p) => `${p.name} NOT original`).join(' · ')
                  : 'Non-original / not authentic'}
              </>
            ) : security?.oemIsAuthentic === true ? (
              'All original'
            ) : (
              'Not checked'
            )}
          </span>
        </div>

        <div
          className={cn(
            'flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5',
            accountFound
              ? 'bg-red-100 text-red-900 ring-1 ring-red-300 font-semibold'
              : 'text-slate-800',
          )}
        >
          <span className="font-medium">👤 Account</span>
          <span>
            {accountFound
              ? '⚠ Account still on device'
              : (security?.associatedAccount ?? '').toLowerCase() === 'no'
                ? 'No account found'
                : 'Not checked'}
          </span>
        </div>
      </div>

      <div className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-800">
        <p>
          Tests: {passed} passed · {failedCount} failed · {skipped} skipped
        </p>
        {failed.length > 0 ? (
          <p className="mt-1 text-red-700">
            {failed.slice(0, 12).map((t) => `✗ ${t.name}`).join('   ')}
          </p>
        ) : null}
      </div>

      {grading &&
      [grading.screen, grading.back, grading.lcd, grading.phoneCase].some(
        (n) => typeof n === 'number' && n > 0,
      ) ? (
        <div className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-800">
          Grading:{' '}
          {grading.screen != null && grading.screen > 0
            ? `Screen ${grading.screen}/5`
            : null}
          {grading.back != null && grading.back > 0
            ? ` · Back ${grading.back}/5`
            : null}
          {grading.lcd != null && grading.lcd > 0 ? ` · LCD ${grading.lcd}/5` : null}
          {grading.phoneCase != null && grading.phoneCase > 0
            ? ` · Case ${grading.phoneCase}/5`
            : null}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
        {htmlUrl ? (
          <a
            href={htmlUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            View HTML Report
          </a>
        ) : null}
        {pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Download PDF
          </a>
        ) : reportUrl && !htmlUrl ? (
          <a
            href={reportUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            View Report ↗
          </a>
        ) : null}
        {suggested && onApplyGrading ? (
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  `Apply M360 grading as device condition: ${suggested}? Staff should confirm this matches the physical device.`,
                )
              ) {
                onApplyGrading(suggested);
              }
            }}
            className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10"
          >
            Use M360 grading
          </button>
        ) : null}
        <button
          type="button"
          onClick={onRemove}
          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function RetrieveControls({
  label,
  ticketId,
  ticketDeviceId,
  defaultQuery,
  diagnosticType,
  onDone,
}: {
  label: string;
  ticketId: string;
  ticketDeviceId?: string;
  defaultQuery?: string | null;
  diagnosticType: 'pre' | 'post';
  onDone: () => void;
}) {
  const [query, setQuery] = useState(defaultQuery ?? '');
  const [lookupKind, setLookupKind] = useState<M360LookupKind>('auto');

  useEffect(() => {
    if (defaultQuery) setQuery(defaultQuery);
  }, [defaultQuery]);

  const detected = useMemo(
    () => (query.trim() ? detectLookupKind(query) : 'imei'),
    [query],
  );
  const effectiveKind = lookupKind === 'auto' ? detected : lookupKind;

  const retrieveMut = useMutation({
    mutationFn: () => {
      if (!query.trim()) throw new Error('Enter an IMEI, M360 ID, session ID, or serial');
      return retrieveM360Diagnostic({
        ticketId,
        ticketDeviceId,
        query: query.trim(),
        lookupKind: lookupKind === 'auto' ? 'auto' : lookupKind,
        diagnosticType,
      });
    },
    onSuccess: () => {
      toastSuccess(`${label} diagnostic retrieved`);
      onDone();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <div className="rounded-xl border border-dashed border-slate-300 p-4">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{label}</h4>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
          placeholder="IMEI, M360 ID, session ID, or serial"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className="rounded-lg border border-slate-300 px-2 py-2 text-sm"
          value={lookupKind}
          onChange={(e) => setLookupKind(e.target.value as M360LookupKind)}
          title="Lookup type (override auto-detect)"
        >
          <option value="auto">Auto ({effectiveKind})</option>
          <option value="imei">IMEI</option>
          <option value="m360id">M360 ID</option>
          <option value="sessionId">Session ID</option>
          <option value="serial">Serial</option>
        </select>
        <button
          type="button"
          disabled={!query.trim() || retrieveMut.isPending}
          onClick={() => retrieveMut.mutate()}
          className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {retrieveMut.isPending ? 'Retrieving…' : 'Retrieve'}
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Detected as <span className="font-medium">{effectiveKind}</span>
        {lookupKind !== 'auto' ? ' (manual override)' : ''}
      </p>
    </div>
  );
}

function ComparisonView({ pre, post }: { pre: M360DiagnosticRow; post: M360DiagnosticRow }) {
  const preFailed = new Set(rowTests(pre).filter((t) => t.status === 'fail').map((t) => t.name));
  const postFailed = new Set(rowTests(post).filter((t) => t.status === 'fail').map((t) => t.name));
  const fixed = [...preFailed].filter((n) => !postFailed.has(n));

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
      <h4 className="font-semibold text-slate-950">Diagnostic Comparison</h4>
      <div className="mt-3 grid grid-cols-4 gap-2 text-sm">
        <div className="font-medium text-slate-500" />
        <div className="font-semibold">PRE</div>
        <div className="font-semibold">POST</div>
        <div className="font-semibold">Δ</div>
        <div className="text-slate-500">Passed</div>
        <div>{pre.passedTests ?? 0}</div>
        <div>{post.passedTests ?? 0}</div>
        <div>+{(post.passedTests ?? 0) - (pre.passedTests ?? 0)}</div>
        <div className="text-slate-500">Failed</div>
        <div>{pre.failedTests ?? 0}</div>
        <div>{post.failedTests ?? 0}</div>
        <div>{(post.failedTests ?? 0) - (pre.failedTests ?? 0)}</div>
      </div>
      {fixed.length > 0 ? (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase text-emerald-700">Fixed by repair</p>
          <ul className="mt-1 text-sm text-slate-700">
            {fixed.map((n) => (
              <li key={n}>✅ {n}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function M360DiagnosticsPanel({
  ticketId,
  ticketDeviceId,
  imei,
  serial,
  embedded,
  onApplyGrading,
}: {
  ticketId: string;
  ticketDeviceId?: string;
  imei?: string | null;
  serial?: string | null;
  embedded?: boolean;
  onApplyGrading?: (condition: 'excellent' | 'good' | 'fair' | 'poor' | 'broken') => void;
}) {
  const queryClient = useQueryClient();
  const statusQ = useQuery({ queryKey: ['m360-status'], queryFn: getM360Status });
  const diagQ = useQuery({
    queryKey: ['m360-ticket', ticketId],
    queryFn: () => getTicketM360Diagnostics(ticketId),
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => deleteM360Diagnostic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['m360-ticket', ticketId] });
      toastSuccess('Diagnostic removed');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['m360-ticket', ticketId] });
    void queryClient.invalidateQueries({ queryKey: ['upsell'] });
  };

  const pre = diagQ.data?.pre ?? null;
  const post = diagQ.data?.post ?? null;
  const defaultQuery = imei?.trim() || serial?.trim() || '';

  return (
    <section className={embedded ? '' : 'rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'}>
      {embedded ? null : (
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-800">
          M360 Diagnostics
        </h3>
      )}

      {statusQ.isLoading || diagQ.isLoading ? (
        <p className="mt-3 text-sm text-slate-500">Loading…</p>
      ) : !statusQ.data?.configured ? (
        <p className="mt-3 text-sm text-slate-500">
          M360 not configured. Set <code className="text-xs">M360_API_KEY</code> to{' '}
          <code className="text-xs">authCode-authToken</code> and{' '}
          <code className="text-xs">M360_API_URL=https://m360soft.com/api/customer/v2</code>
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          {statusQ.data.keyFormatOk === false && statusQ.data.keyFormatWarning ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {statusQ.data.keyFormatWarning}
            </p>
          ) : null}
          {statusQ.data.testingMode ? (
            <p className="text-xs text-slate-500">
              Testing mode on — API calls send <code>testing: true</code> (fake data).
            </p>
          ) : null}
          {pre && post ? <ComparisonView pre={pre} post={post} /> : null}
          {pre ? (
            <DiagnosticCard
              title="M360 DIAGNOSTIC"
              row={pre}
              onRemove={() => removeMut.mutate(pre.id)}
              onApplyGrading={onApplyGrading}
            />
          ) : (
            <RetrieveControls
              label="Pre-Repair"
              ticketId={ticketId}
              ticketDeviceId={ticketDeviceId}
              defaultQuery={defaultQuery}
              diagnosticType="pre"
              onDone={refresh}
            />
          )}
          {post ? (
            <DiagnosticCard
              title="M360 POST-REPAIR"
              row={post}
              onRemove={() => removeMut.mutate(post.id)}
              onApplyGrading={onApplyGrading}
            />
          ) : (
            <RetrieveControls
              label="Post-Repair"
              ticketId={ticketId}
              ticketDeviceId={ticketDeviceId}
              defaultQuery={defaultQuery}
              diagnosticType="post"
              onDone={refresh}
            />
          )}
        </div>
      )}
    </section>
  );
}
